// Где и чем работает человек — атрибутами на корне страницы.
//
// ## Почему атрибуты, а не хук с состоянием
//
// Три причины, и каждая замерена в этом продукте.
//
// **Поворот планшета не должен перерисовывать сетку на 281 строку.** Хук с состоянием
// перестраивает всё дерево под собой; атрибут меняется на `<html>`, а перекладывают стили —
// React об этом не знает вовсе.
//
// **Проверять снаружи.** Сквозная проверка читает атрибут ровно так же, как уже читает
// `data-behaviour` у ячейки сетки и `data-печать` у печатной формы: «в узком режиме колонка
// списка занимает всю ширину» проверяется без единого мока.
//
// **Признаки независимы.** Планшет с подключённой клавиатурой — это `тач` И `клавиатура`
// одновременно: крупные цели касания остаются, а подписи сочетаний клавиш возвращаются.
// Один признак «режим планшета» такое выразить не может, и подписи либо врут, либо пропадают
// у того, кто может их нажать.
//
// Устроено по образцу темы (`shared/theme/index.ts`): применение — функцией на `<html>`,
// слежение — через `matchMedia`, подписка для разметки — отдельным хуком.

import { useSyncExternalStore } from 'react';

import { ПОРОГ_ШИРОКО, ПОРОГ_УЗКО, ширинаПоЧислу, type Ширина } from './пороги';

export type { Ширина };
export { ПОРОГ_ШИРОКО, ПОРОГ_УЗКО };

/** Чем человек показывает: пальцем или указателем. */
export type Ввод = 'тач' | 'мышь';

export interface Среда {
  ширина: Ширина;
  ввод: Ввод;
  /** Есть ли настоящая клавиатура. Пока не нажали ни одной служебной клавиши — нет. */
  клавиатура: boolean;
}

const КОРЕНЬ = () => document.documentElement;

let текущая: Среда = { ширина: 'широко', ввод: 'мышь', клавиатура: false };
const слушатели = new Set<() => void>();

function объявить(следующая: Среда): void {
  if (
    следующая.ширина === текущая.ширина &&
    следующая.ввод === текущая.ввод &&
    следующая.клавиатура === текущая.клавиатура
  ) {
    return;
  }
  текущая = следующая;
  const корень = КОРЕНЬ();
  корень.dataset.ширина = следующая.ширина;
  корень.dataset.ввод = следующая.ввод;
  if (следующая.клавиатура) {
    корень.dataset.клавиатура = 'есть';
  } else {
    delete корень.dataset.клавиатура;
  }
  for (const слушатель of слушатели) {
    слушатель();
  }
}

/**
 * Служебные клавиши, по которым видно НАСТОЯЩУЮ клавиатуру.
 *
 * Экранная клавиатура планшета таких событий не порождает: она шлёт только сами знаки. Значит
 * подпись «Ctrl K» появляется ровно тогда, когда её есть чем нажать, — а до этого её нет, и
 * человек не читает инструкцию к тому, чего у него не существует.
 */
const СЛУЖЕБНЫЕ = new Set(['Tab', 'Escape', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']);

function этоНастоящаяКлавиатура(событие: KeyboardEvent): boolean {
  return (
    СЛУЖЕБНЫЕ.has(событие.key) ||
    /^F\d{1,2}$/.test(событие.key) ||
    событие.ctrlKey ||
    событие.metaKey ||
    событие.altKey
  );
}

/** Что известно о видимой части окна в этот миг. */
export interface ВидимаяЧасть {
  /** Высота страницы целиком — она НЕ меняется при появлении клавиатуры. */
  высотаСтраницы: number;
  /** Высота того, что человек сейчас видит. */
  высотаВидимого: number;
  /** На сколько видимая часть сдвинута сверху (страница прокручена «под» клавиатуру). */
  сдвигСверху: number;
}

/**
 * Сколько точек окна закрыто снизу экранной клавиатурой.
 *
 * Отдельной функцией, потому что поведение планшета в браузере не воспроизводится: экранной
 * клавиатуры там нет вовсе, и проверить это живым прогоном нечем. Формулу же проверить
 * обязаны — ошибка в ней означает либо поле, оставшееся под клавиатурой, либо пустую полосу
 * в треть экрана.
 *
 * Слагаемое `сдвигСверху` обязательно: при прокрученной странице видимая часть ещё и сдвинута,
 * и без него отступ получается больше нужного ровно на величину сдвига.
 *
 * Порог в 80 точек отсекает погрешность округления при масштабировании: клавиатуры такой
 * высоты не бывает, а дрожание переменной на пару точек дало бы дёрганье всей раскладки.
 */
export function высотаКлавиатуры(часть: ВидимаяЧасть): number {
  const закрыто = Math.round(
    часть.высотаСтраницы - (часть.высотаВидимого + часть.сдвигСверху),
  );
  return закрыто > 80 ? закрыто : 0;
}

/**
 * Начать следить за средой. Зовётся один раз при запуске, до первой отрисовки.
 *
 * Возвращает функцию отписки — она нужна проверкам, а в продукте слежение живёт весь сеанс.
 */
export function следитьЗаСредой(): () => void {
  const узко = window.matchMedia(`(max-width: ${ПОРОГ_УЗКО - 1}px)`);
  const широко = window.matchMedia(`(min-width: ${ПОРОГ_ШИРОКО}px)`);
  // `any-pointer`, а не `pointer`: у планшета с подключённой мышью грубый указатель остаётся
  // одним из доступных, и цели касания должны остаться крупными — пальцем по экрану он всё
  // равно тычет, мышь тут вторым способом.
  const грубый = window.matchMedia('(any-pointer: coarse)');

  const пересчитать = () => {
    const ввод: Ввод = грубый.matches ? 'тач' : 'мышь';
    объявить({
      ширина: ширинаПоЧислу(window.innerWidth),
      ввод,
      // У точного указателя клавиатура есть всегда: это компьютер. Ждать там первого нажатия
      // Tab означало бы прятать подпись «Ctrl K» от того, кто как раз может её нажать, — а
      // человек, не знающий о сочетании, никогда его и не нажмёт, то есть подпись не появится
      // вовсе. Ждём нажатия ТОЛЬКО на планшете, где клавиатура бывает, а бывает и нет.
      клавиатура: текущая.клавиатура || ввод === 'мышь',
    });
  };

  const наКлавише = (событие: KeyboardEvent) => {
    if (!текущая.клавиатура && этоНастоящаяКлавиатура(событие)) {
      объявить({ ...текущая, клавиатура: true });
    }
  };

  // ── Экранная клавиатура ─────────────────────────────────────────────────────────────────
  //
  // На планшете клавиатура НЕ уменьшает окно: страница остаётся прежней высоты, а нижняя её
  // треть просто оказывается закрытой. Поле ввода, стоящее внизу — фраза для входа, сумма в
  // мастере выдачи, поле в подвале панели, — уходит под клавиатуру, и человек печатает
  // вслепую либо не может дотянуться до кнопки. Это самый частый признак «внутри сайт» в
  // формах, и лечится он только здесь: сама вёрстка о клавиатуре не знает ничего.
  //
  // Высота считается как «сколько окна закрыто снизу», а не как «высота клавиатуры»: при
  // прокрученной странице видимая часть ещё и сдвинута сверху (`offsetTop`), и без этого
  // слагаемого отступ получается больше нужного ровно на величину сдвига.
  const окно = window.visualViewport;

  const пересчитатьКлавиатуру = () => {
    if (окно === null || окно === undefined) {
      return;
    }
    const высота = высотаКлавиатуры({
      высотаСтраницы: window.innerHeight,
      высотаВидимого: окно.height,
      сдвигСверху: окно.offsetTop,
    });
    КОРЕНЬ().style.setProperty('--bark-клавиатура', `${высота}px`);
  };

  /**
   * Подтянуть поле к глазам, когда в него встали.
   *
   * Уменьшения окна мало: в длинной форме поле может стоять посреди прокрутки, и после
   * пересчёта высоты оно окажется видимым лишь случайно. Ждём кадр — к этому моменту
   * клавиатура уже объявила свою высоту, и прокрутка считает по правильному окну.
   */
  const кГлазам = (событие: FocusEvent) => {
    const цель = событие.target;
    if (!(цель instanceof HTMLElement)) {
      return;
    }
    if (!цель.matches('input, textarea, [contenteditable="true"]')) {
      return;
    }
    // Только под палец: на компьютере клавиатура ничего не закрывает, а рывок прокрутки при
    // каждом переходе по Tab — это дёрганье экрана на ровном месте.
    if (текущая.ввод !== 'тач') {
      return;
    }
    window.setTimeout(() => {
      цель.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 250);
  };

  окно?.addEventListener('resize', пересчитатьКлавиатуру);
  окно?.addEventListener('scroll', пересчитатьКлавиатуру);
  document.addEventListener('focusin', кГлазам);
  пересчитатьКлавиатуру();

  узко.addEventListener('change', пересчитать);
  широко.addEventListener('change', пересчитать);
  грубый.addEventListener('change', пересчитать);
  // Окно на планшете меняет размер не только поворотом: половина экрана в разделённом виде это
  // тот же самый случай, и медиазапроса «поворот» для него не существует.
  window.addEventListener('resize', пересчитать);
  window.addEventListener('keydown', наКлавише, { capture: true });

  пересчитать();

  return () => {
    document.removeEventListener('focusin', кГлазам);
    окно?.removeEventListener('resize', пересчитатьКлавиатуру);
    окно?.removeEventListener('scroll', пересчитатьКлавиатуру);
    узко.removeEventListener('change', пересчитать);
    широко.removeEventListener('change', пересчитать);
    грубый.removeEventListener('change', пересчитать);
    window.removeEventListener('resize', пересчитать);
    window.removeEventListener('keydown', наКлавише, { capture: true });
  };
}

function подписаться(слушатель: () => void): () => void {
  слушатели.add(слушатель);
  return () => {
    слушатели.delete(слушатель);
  };
}

/**
 * Среда для разметки.
 *
 * Зовётся ТОЛЬКО там, где ветка в коде неизбежна: высота строки для расчёта окна прокрутки,
 * состав каталога колонок, поведение первого нажатия по ячейке. Всё, что решается стилями,
 * решается стилями — вариантами `узко:`, `тач:` и `клавиша:`.
 */
export function useСреда(): Среда {
  return useSyncExternalStore(
    подписаться,
    () => текущая,
    () => текущая,
  );
}
